import React, { useState } from 'react';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { getCurriculum, getTrainees, enrollTechnician } from '../services/api';

export default function Training() {
  const { data: currData, loading: loadingCurr } = useAPI(getCurriculum);
  const { data: traineeData, loading: loadingTrainees, reload } = useAPI(getTrainees);
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    technician_id: '', name: '', region: 'israel', experience_level: 'rookie',
    mentor_name: '',
  });
  const [enrolling, setEnrolling] = useState(false);

  if (loadingCurr || loadingTrainees) {
    return <div style={{ padding: 40, color: '#999' }}>Loading training data...</div>;
  }

  const modules = currData?.modules || [];
  const trainees = traineeData?.trainees || [];
  const totalHours = currData?.total_hours || 0;

  const handleEnroll = async (e) => {
    e.preventDefault();
    setEnrolling(true);
    try {
      await enrollTechnician({
        ...enrollForm,
        mentor_id: enrollForm.mentor_name ? `mentor-${enrollForm.mentor_name}` : null,
        mentor_name: enrollForm.mentor_name || null,
      });
      reload();
      setShowEnroll(false);
      setEnrollForm({
        technician_id: '', name: '', region: 'israel', experience_level: 'rookie',
        mentor_name: '',
      });
    } catch (err) {
      alert('Enrollment error: ' + err.message);
    }
    setEnrolling(false);
  };

  const categories = [...new Set(modules.map(m => m.category))];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Training & Onboarding</h1>
          <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
            {modules.length} modules | {totalHours}h total curriculum
          </p>
        </div>
        <button onClick={() => setShowEnroll(!showEnroll)} style={styles.enrollBtn}>
          {showEnroll ? 'Cancel' : '+ Enroll Technician'}
        </button>
      </div>

      {/* Enroll form */}
      {showEnroll && (
        <form onSubmit={handleEnroll} style={styles.enrollForm}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Enroll New Technician</h3>
          <div style={styles.formGrid}>
            <input
              placeholder="Technician ID"
              value={enrollForm.technician_id}
              onChange={e => setEnrollForm({ ...enrollForm, technician_id: e.target.value })}
              required style={styles.input}
            />
            <input
              placeholder="Full Name"
              value={enrollForm.name}
              onChange={e => setEnrollForm({ ...enrollForm, name: e.target.value })}
              required style={styles.input}
            />
            <select
              value={enrollForm.region}
              onChange={e => setEnrollForm({ ...enrollForm, region: e.target.value })}
              style={styles.input}
            >
              <option value="israel">Israel</option>
              <option value="india">India</option>
            </select>
            <select
              value={enrollForm.experience_level}
              onChange={e => setEnrollForm({ ...enrollForm, experience_level: e.target.value })}
              style={styles.input}
            >
              <option value="rookie">Rookie (0-3 months)</option>
              <option value="junior">Junior (3-12 months)</option>
              <option value="experienced">Experienced (1-3 years)</option>
              <option value="senior">Senior (3+ years)</option>
            </select>
            <input
              placeholder="Mentor Name (optional)"
              value={enrollForm.mentor_name}
              onChange={e => setEnrollForm({ ...enrollForm, mentor_name: e.target.value })}
              style={styles.input}
            />
            <button type="submit" disabled={enrolling} style={styles.submitBtn}>
              {enrolling ? 'Enrolling...' : 'Enroll'}
            </button>
          </div>
        </form>
      )}

      {/* KPI cards */}
      <div style={styles.cardGrid}>
        <StatCard label="Training Modules" value={modules.length} color="#6c5ce7" />
        <StatCard label="Active Trainees" value={traineeData?.active || 0} color="#0984e3" />
        <StatCard label="Completed" value={traineeData?.completed || 0} color="#00b894" />
        <StatCard label="Total Curriculum" value={`${totalHours}h`} color="#e17055" />
      </div>

      <div style={styles.twoCol}>
        {/* Trainees */}
        <div>
          <h2 style={styles.sectionTitle}>Trainees</h2>
          {trainees.length > 0 ? (
            <DataTable
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'region', label: 'Region' },
                { key: 'level', label: 'Level' },
                {
                  key: 'progress_pct',
                  label: 'Progress',
                  render: v => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={styles.progressBg}>
                        <div style={{
                          ...styles.progressFill,
                          width: `${v}%`,
                          background: v >= 100 ? '#00b894' : v >= 50 ? '#6c5ce7' : '#e17055',
                        }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{v}%</span>
                    </div>
                  ),
                },
                {
                  key: 'completed',
                  label: 'Modules',
                  render: (v, row) => `${v}/${row.total}`,
                },
                { key: 'certifications', label: 'Certs', align: 'center' },
                { key: 'mentor', label: 'Mentor', render: v => v || '-' },
                { key: 'days', label: 'Days', align: 'right' },
              ]}
              data={trainees}
            />
          ) : (
            <div style={styles.emptyBox}>
              No trainees enrolled yet. Click "Enroll Technician" to get started.
            </div>
          )}
        </div>

        {/* Curriculum */}
        <div>
          <h2 style={styles.sectionTitle}>Curriculum</h2>
          {categories.map(cat => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={styles.catHeader}>
                {cat.replace('_', ' ').toUpperCase()}
              </div>
              {modules.filter(m => m.category === cat).map(mod => (
                <div key={mod.id} style={styles.moduleCard}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{mod.title}</div>
                      <div style={{ fontSize: 11, color: '#636e72', marginTop: 2 }}>
                        {mod.title_he} / {mod.title_hi}
                      </div>
                    </div>
                    <span style={{
                      ...styles.levelBadge,
                      background: mod.level === 'rookie' ? '#d4edda' :
                                  mod.level === 'junior' ? '#fff3cd' :
                                  mod.level === 'experienced' ? '#e8daef' : '#f8d7da',
                      color: mod.level === 'rookie' ? '#155724' :
                             mod.level === 'junior' ? '#856404' :
                             mod.level === 'experienced' ? '#6c3483' : '#721c24',
                    }}>
                      {mod.level}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: '#636e72', marginTop: 6 }}>
                    {mod.description}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={styles.miniTag}>{mod.total_steps} steps</span>
                    <span style={styles.miniTag}>{mod.estimated_hours}h</span>
                    {mod.has_certification && (
                      <span style={{ ...styles.miniTag, background: '#d4edda', color: '#155724' }}>
                        Certification
                      </span>
                    )}
                    {mod.step_types?.includes('quiz') && (
                      <span style={{ ...styles.miniTag, background: '#fff3cd', color: '#856404' }}>
                        Quiz
                      </span>
                    )}
                    {mod.step_types?.includes('field_exercise') && (
                      <span style={{ ...styles.miniTag, background: '#e8daef', color: '#6c3483' }}>
                        Field Exercise
                      </span>
                    )}
                  </div>
                  {mod.prerequisites?.length > 0 && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
                      Prerequisites: {mod.prerequisites.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, margin: '20px 0' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  enrollBtn: {
    padding: '8px 16px', borderRadius: 6, border: 'none',
    background: '#6c5ce7', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
  },
  enrollForm: {
    padding: 20, background: '#fff', borderRadius: 10, marginTop: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  formGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
  },
  input: {
    padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
    fontSize: 13, outline: 'none',
  },
  submitBtn: {
    padding: '8px 16px', borderRadius: 6, border: 'none',
    background: '#00b894', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
  },
  catHeader: {
    fontSize: 11, fontWeight: 700, color: '#6c5ce7', letterSpacing: '1px',
    marginBottom: 8, padding: '4px 0', borderBottom: '1px solid #f0f0f0',
  },
  moduleCard: {
    padding: 14, background: '#fff', borderRadius: 8, marginBottom: 8,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  levelBadge: {
    padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
    flexShrink: 0,
  },
  miniTag: {
    padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
    background: '#f0f0f0', color: '#636e72',
  },
  progressBg: {
    width: 60, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden',
  },
  progressFill: {
    height: '100%', borderRadius: 3, transition: 'width 0.3s',
  },
  emptyBox: {
    padding: 20, background: '#fff', borderRadius: 10, fontSize: 13,
    color: '#999', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
};
